import { Loader2 } from "lucide-react";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { ESTIMATE_CONST } from "@/constants/estimates.constant";
import useQueryParams from "@/hooks/useQueryParams";
import { IItems } from "@/interfaces/items.interface";
import {
  MainCost,
  TEstimationData,
} from "@/interfaces/project-register.interface";
import { WasteFormat } from "@/interfaces/wastetype.interface";
import { useGetSingleClaimsEstimatesQuery } from "@/redux/api/claimsQuery";
import { useGetItemsQuery } from "@/redux/api/itemQuery";
import { useGetSingleEstimateQuery } from "@/redux/api/prQuery";
import { useGetWasteFormatQuery } from "@/redux/api/wasteManageSummaryandSettings";

export type SelectedData = {
  mainCost: MainCost[];
  additionalCost: MainCost[];
};

interface ReviewSheetContextType {
  selectedData: SelectedData | null;
  setDetailSelected: (
    mainCostIndex: number,
    detailIndex: number,
    isOptional: boolean,
  ) => void;
  itemsList: IItems[];
  review: string;
  originalData: TEstimationData | undefined;
  letterHead: WasteFormat;
  hideAmount: boolean;
  setIsHideAmount: (value: boolean) => void;
}

const ReviewSheetContext = createContext<ReviewSheetContextType | undefined>(
  undefined,
);

export const ReviewSheetProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [params] = useSearchParams();
  const leadsId = params.get("leadsId");
  const estimationId = params.get("estimationId");
  const [selectedData, setSelectedData] = useState<SelectedData | null>(null);
  const [review, setReview] = useState<string>("");
  const [originalData, setOriginalData] = useState<TEstimationData>();
  const [hideAmount, setIsHideAmount] = useState<boolean>(false);

  const { data: itemsList, isLoading: isItemsFetching } = useGetItemsQuery({
    filter: "?type=ESTIMATION",
  });

  const { keys } = useQueryParams();
  const claimsId = keys?.rowId as string;
  const claimsEstimationId = keys?.claimsEstimationId as string;

  const { data: res } = useGetWasteFormatQuery({});
  const letterHead = res as WasteFormat;

  const { data: dataEstimation, isLoading } = useGetSingleEstimateQuery(
    {
      id: leadsId || "",
      estimateId: estimationId || "",
    },
    {
      skip: !leadsId || !estimationId || !!claimsId,
    },
  );

  const { data: singleClaimData, isLoading: isClaimsEstimateLoading } =
    useGetSingleClaimsEstimatesQuery(
      { claimsId, estimateId: estimationId || claimsEstimationId || "" },
      {
        skip: !claimsId,
      },
    );

  const estimationData = useMemo(() => {
    if (claimsId) {
      return singleClaimData;
    }

    return dataEstimation;
  }, [claimsId, dataEstimation, singleClaimData]);

  // const estimationData = claimsId ? singleClaimData : dataEstimation;

  const setDetailSelected = (
    mainCostIndex: number,
    detailIndex: number,
    isOptional: boolean,
  ): void => {
    if (!selectedData) {
      return;
    }

    if (isOptional) {
      const additionalCost = selectedData.additionalCost.map(
        (mainCost, index) => {
          if (index === mainCostIndex) {
            return {
              ...mainCost,
              details: mainCost.details.map((detail, detailIndexInMainCost) => {
                if (detailIndexInMainCost === detailIndex) {
                  return {
                    ...detail,
                    selected: !detail.selected,
                  };
                }

                return detail;
              }),
            };
          }

          return mainCost;
        },
      );

      setSelectedData({
        mainCost: selectedData.mainCost,
        additionalCost,
      });

      return;
    }

    const mainCost = selectedData.mainCost.map((mainCost, index) => {
      if (index === mainCostIndex) {
        return {
          ...mainCost,
          details: mainCost.details.map((detail, detailIndexInMainCost) => {
            if (detailIndexInMainCost === detailIndex) {
              return {
                ...detail,
                selected: !detail.selected,
              };
            }

            return detail;
          }),
        };
      }

      return mainCost;
    });

    setSelectedData({
      mainCost,
      additionalCost: selectedData.additionalCost,
    });
  };

  useEffect(() => {
    if (estimationData) {
      const mainData = (estimationData?.data as TEstimationData)
        ?.estimationTable;

      setSelectedData({
        mainCost:
          mainData?.mainCost?.map(
            (item) =>
              ({
                ...item,
                details: item?.details?.map((detail) => ({
                  ...detail,
                  selected: true,
                })),
              }) as MainCost,
          ) || [],
        additionalCost:
          mainData?.optionalCost?.map(
            (item) =>
              ({
                ...item,
                details: item?.details?.map((detail) => ({
                  ...detail,
                  selected: true,
                })),
              }) as MainCost,
          ) || [],
      });
      setReview((estimationData?.data as TEstimationData)?.review);
      setOriginalData(estimationData?.data as TEstimationData);
    }
  }, [estimationData, isLoading, isClaimsEstimateLoading]);

  if (isLoading || isClaimsEstimateLoading || isItemsFetching) {
    return (
      <div className='min-h-screen '>
        <Loader2 className='w-5 h-5 animate-spin' />
      </div>
    );
  }

  return (
    <ReviewSheetContext.Provider
      value={{
        selectedData,
        setDetailSelected,
        itemsList: itemsList?.data || [],
        review,
        originalData,
        letterHead,
        hideAmount,
        setIsHideAmount,
      }}
    >
      {children}
    </ReviewSheetContext.Provider>
  );
};

export const useReviewSheet = (): ReviewSheetContextType => {
  const context = useContext(ReviewSheetContext);
  if (!context) {
    throw new Error(ESTIMATE_CONST.MESSAGES.USE_REVIEW_SHEET);
  }

  return context;
};
